import { RefObject, useLayoutEffect, useMemo, useState } from 'react';

/**
 * Medição de largura de colunas de tabela em grid (at-43).
 *
 * Substitui o efeito que estava copiado quase idêntico em 7 telas (Recepção, Chefe de
 * Oficina, Controle de Liberação, Controle de Produção, Tela de Controle, Usuários e
 * Comissionamentos): mede cada coluna com canvas.measureText usando as fontes reais
 * (computed style do primeiro header e da primeira célula, com fallback), respeita as
 * larguras mínimas, distribui a sobra do container entre as colunas e recalcula em
 * resize (janela e container via ResizeObserver) e quando as fontes terminam de carregar.
 *
 * As telas fornecem `rows` já MEMOIZADO (string[][] — valores renderizados por linha);
 * é isso que permite que as dependências do efeito sejam reais, sem eslint-disable.
 */
type UseTableColumnsArgs = {
  /** Container com scroll horizontal da tabela (era o tableScrollRef das telas). */
  containerRef: RefObject<HTMLElement | null>;
  /** Ref do array de headers (padrão das telas: headerRefs); o primeiro deriva a fonte. */
  headerCellsRef?: RefObject<(HTMLElement | null)[]>;
  headers: string[];
  /** Largura mínima por coluna, em px (mesmo tamanho de `headers`). */
  defaultWidths: number[];
  /**
   * TETO da largura mínima por coluna, em px (`0`/ausente = sem teto). previsao-pecas/at-01.
   *
   * 🔴 Sem isto a tabela cresce com o DADO, não com a tela: a largura mínima de cada coluna é o
   * maior texto que ela contém, então um nome de fornecedor de 100 caracteres vindo do ERP
   * empurra a última coluna para fora do container — e nenhum `defaultWidths` evita, porque ele é
   * só o piso. Com o teto, a soma das mínimas fica limitada por construção e a célula termina em
   * reticências (com o texto inteiro no `title`), que é a regra de célula de UMA linha (12/08/2026).
   * A sobra do container continua sendo distribuída: o teto limita o mínimo, não o máximo.
   */
  maxWidths?: number[];
  /** Valores renderizados, por linha e coluna. Deve vir de um useMemo na tela. */
  rows: string[][];
  /** Quando falso, a medição não roda (ex.: aba inativa). Default: true. */
  enabled?: boolean;
  /** Seletor da primeira célula de dados dentro do container, para derivar a fonte. */
  cellSelector?: string;
  fallbackHeaderFont?: string;
  fallbackCellFont?: string;
  /** Folga somada à largura final do grid (as telas atuais usam 60). */
  extraPadding?: number;
  /**
   * Valor extra que força re-medição/re-observação quando muda (ex.: aba ativa, quando a
   * troca de aba remonta o container e o ResizeObserver precisa religar no elemento novo).
   */
  remeasureKey?: unknown;
};

export function useTableColumns({
  containerRef,
  headerCellsRef,
  headers,
  defaultWidths,
  maxWidths,
  rows,
  enabled = true,
  cellSelector = '.Tabela:not(.TabelaHeader) .Data',
  fallbackHeaderFont = '600 14px Roboto',
  fallbackCellFont = '400 12px Roboto',
  extraPadding = 60,
  remeasureKey,
}: UseTableColumnsArgs) {
  const [tableColumns, setTableColumns] = useState<string>(
    headers.map(() => '140px').join(' ')
  );
  const [tableWidth, setTableWidth] = useState<string>('100%');

  // Chaves estáveis para arrays que as telas declaram inline (recriados a cada render):
  // sem isso, o efeito derrubaria e recriaria o ResizeObserver em todo render do chamador.
  const defaultWidthsKey = defaultWidths.join(',');
  const maxWidthsKey = maxWidths?.join(',') ?? '';
  const headersKey = JSON.stringify(headers);

  useLayoutEffect(() => {
    if (!enabled) return;

    const defaults = defaultWidthsKey.split(',').map(Number);
    const tetos = maxWidthsKey ? maxWidthsKey.split(',').map(Number) : [];
    const headers: string[] = JSON.parse(headersKey);

    const updateTableColumns = () => {
      // Sem o container montado, o cálculo cairia no fallback de largura mínima.
      if (!containerRef.current) return;

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) return;

      const firstHeader = headerCellsRef?.current?.[0] ?? null;
      const firstCell = containerRef.current.querySelector(cellSelector) as HTMLElement | null;

      const headerStyle = firstHeader ? window.getComputedStyle(firstHeader) : null;
      const cellStyle = firstCell ? window.getComputedStyle(firstCell) : null;

      const headerFont = headerStyle
        ? `${headerStyle.fontWeight} ${headerStyle.fontSize} ${headerStyle.fontFamily}`
        : fallbackHeaderFont;
      const cellFont = cellStyle
        ? `${cellStyle.fontWeight} ${cellStyle.fontSize} ${cellStyle.fontFamily}`
        : fallbackCellFont;

      const minWidths = headers.map((header, index) => {
        context.font = headerFont;
        const headerWidth = Math.ceil(context.measureText(header).width) + 32;

        const contentWidth = rows.reduce((maxWidth, row) => {
          context.font = cellFont;
          const valueWidth = Math.ceil(context.measureText(String(row[index])).width) + 32;
          return Math.max(maxWidth, valueWidth);
        }, 0);

        const minimo = Math.max(defaults[index] ?? 0, headerWidth, contentWidth);
        const teto = tetos[index];
        return teto ? Math.min(minimo, teto) : minimo;
      });

      const minGridWidth = minWidths.reduce((total, width) => total + width, 0);
      const containerWidth = containerRef.current.clientWidth ?? minGridWidth + extraPadding;
      const availableGridWidth = Math.max(containerWidth - extraPadding, 0);
      const finalGridWidth = Math.max(minGridWidth, availableGridWidth);
      const extraWidth = finalGridWidth - minGridWidth;
      const extraPerColumn = Math.floor(extraWidth / headers.length);
      const remainder = extraWidth % headers.length;

      setTableColumns(
        minWidths
          .map((width, index) => `${width + extraPerColumn + (index < remainder ? 1 : 0)}px`)
          .join(' ')
      );
      setTableWidth(`${finalGridWidth + extraPadding}px`);
    };

    const resizeObserver = new ResizeObserver(updateTableColumns);

    updateTableColumns();
    document.fonts.ready.then(updateTableColumns);
    window.addEventListener('resize', updateTableColumns);

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateTableColumns);
    };
  }, [
    enabled,
    rows,
    headersKey,
    defaultWidthsKey,
    maxWidthsKey,
    containerRef,
    headerCellsRef,
    cellSelector,
    fallbackHeaderFont,
    fallbackCellFont,
    extraPadding,
    remeasureKey,
  ]);

  return useMemo(() => ({ tableColumns, tableWidth }), [tableColumns, tableWidth]);
}
